import { Box, Button, Card, CardContent, CardHeader, Chip, Dialog, DialogActions, DialogContent, DialogTitle, Grid, MenuItem, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, ToggleButton, ToggleButtonGroup } from "@mui/material";
import { useCallback, useEffect, useMemo, useState } from "react";
import api from "../../../../../Services/api";
import { formatCurrency } from "../../../../../Utils/formatters";

const defaultRange = {
  min: 0,
  max: 0,
  operators: [] as string[]
}

const formatNumber = (n: string) => {
  const rawText = n.replace(/\D/gi, '') || '0'

  return parseInt(rawText);
}

const paymentMethods = ['pix', 'boleto', 'p2p'] as const;

type PaymentMethodType = typeof paymentMethods[number];

const RangeConfiguration = () => {

  const [operators, setOperators] = useState([]);
  const [ranges, setRanges] = useState<{
    [key in PaymentMethodType]: PaymentRange[]
  }>({
    boleto: [],
    p2p: [],
    pix: []
  });

  const [open, setOpen] = useState(false);

  const [selectedMethod, setSelectedMethod] = useState<PaymentMethodType>('pix');

  const selectedRanges = useMemo(() => {
    return [...(ranges[selectedMethod] || [])].sort((a, b) => a.min - b.min);
  }, [ranges, selectedMethod])

  const [errors, setErrors] = useState<Partial<Record<keyof PaymentRange, string>>>({})

  const [range, setRange] = useState({ ...defaultRange });

  const handleAdd = () => {

    const lastRange = selectedRanges[selectedRanges.length - 1];

    const currentRange = { ...defaultRange };

    if (lastRange) {
      currentRange.min = lastRange.max + 1;
    }
    setErrors({});
    setRange(currentRange);
    setOpen(true);
  }

  const getRanges = () => {
    api.get('/PaymentDepartament/limits')
      .then(e => setRanges(e.data || {}));
  }

  const getOperators = () => {
    api.get(`/operators/paymentdepartament`)
      .then(e => setOperators(e.data || []));
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const errors = {} as Partial<Record<keyof PaymentRange, string>>;

    const lastRange = selectedRanges[selectedRanges.length - 1];

    if (range.min >= range.max) {
      errors.min = 'Valor inválido'
    }
    else if (lastRange && range.min <= lastRange.max) {
      errors.min = `Valor mínimo: ${formatCurrency(range.max + 1)}`
    }

    if (range.max <= range.min) {
      errors.max = 'Valor inválido'
    }

    setErrors(errors);

    if (Object.keys(errors).length > 0) return;

    const newRanges = { ...ranges };

    newRanges[selectedMethod] = [...selectedRanges, { ...range }];

    await api.post('/PaymentDepartament/limits', newRanges);

    setRanges(newRanges);
    setOpen(false);
  }

  const getOperatorById = useCallback((id: string) => (
    operators.find(e => e.id === id)
  ), [operators]);

  useEffect(() => {
    getRanges();
    getOperators();
  }, [])

  return (
    <>
      <Dialog maxWidth={'lg'} open={open} onClose={() => setOpen(false)} container={document.body}>
        <DialogTitle>Faixa de pagamento</DialogTitle>
        <DialogContent>
          <Grid container sx={{ mt: 2 }} spacing={2}>
            <Grid item xs={12}>
              <TextField
                inputMode='decimal'
                value={formatCurrency(range.min)}
                onChange={e => setRange(oldRange => ({ ...oldRange, min: formatNumber(e.target.value) }))}
                fullWidth
                label="Valor Mínimo"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                inputMode='decimal'
                value={formatCurrency(range.max)}
                onChange={e => setRange(oldRange => ({ ...oldRange, max: formatNumber(e.target.value) }))}
                fullWidth
                label="Valor Máximo"
                error={!!errors.max}
                helperText={errors.max}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Operadores"
                select
                fullWidth
                value={range.operators}
                onChange={e => setRange(oldRange => ({ ...oldRange, operators: e.target.value as any }))}
                SelectProps={{
                  multiple: true,
                  renderValue: (selecteds: string[]) => (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {selecteds.map(id => (
                        <Chip key={id} label={getOperatorById(id)?.name} />
                      ))}
                    </Box>
                  )
                }}
              >
                {
                  operators.map(op => (
                    <MenuItem key={op.id} value={op.id}>{op.name}</MenuItem>
                  ))
                }
              </TextField>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleSubmit} type='button'>Salvar</Button>
        </DialogActions>
      </Dialog>

      <Box sx={{ mb: 2 }}>
        <ToggleButtonGroup exclusive value={selectedMethod} onChange={(e, val) => setSelectedMethod(val)}>
          {
            paymentMethods.map(e => (
              <ToggleButton type='button' value={e} key={e}>{e}</ToggleButton>
            ))
          }
        </ToggleButtonGroup>
      </Box>

      <Card>
        <CardHeader title={`Faixas de pagamento por produto`} />
        <CardContent sx={{ p: 0 }}>
          <Box sx={{ px: 2 }} display='flex' justifyContent='flex-end'>
            <Button onClick={handleAdd}>Adicionar</Button>
          </Box>

          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Valor Minímo</TableCell>
                  <TableCell>Valor Máximo</TableCell>
                  <TableCell>Operadores Necessários</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {
                  selectedRanges.map((range, i) => (
                    <TableRow key={i}>
                      <TableCell>{formatCurrency(range.min)}</TableCell>
                      <TableCell>{formatCurrency(range.max)}</TableCell>
                      <TableCell>
                        {range.operators.map(id => getOperatorById(id)?.name).join(', ')}
                        {range.operators.length === 0 && 'Não precisa de aprovação'}
                      </TableCell>
                    </TableRow>
                  ))
                }
                {
                  selectedRanges.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={999} style={{ textAlign: 'center' }}>Adicione uma faixa para essa modalidade</TableCell>
                    </TableRow>
                  )
                }
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>

      </Card>
    </>
  )
}

export { RangeConfiguration };

